import { Box, HStack, Spinner, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"

import { OrganisationsService } from "@/client"
import type { ResourcePermissionRead, UserPublic } from "@/client"
import { Badge } from "@/components/ui/badge"
import { useSemanticColors } from "@/hooks/useSemanticColors"

interface UserOrganizationsListProps {
  user: UserPublic
}

const getRoleFromPermissions = (
  permissions: ResourcePermissionRead[],
): string => {
  // Check if user has admin permissions (full CRUD on organization/user resources)
  const hasAdminPerms = permissions.some(
    (p) =>
      (p.resource_type === "organisation" || p.resource_type === "user") &&
      p.permission >= 15, // 15 = 1111 in binary (all permissions)
  )
  if (hasAdminPerms) return "admin"

  // Check if user has write permissions
  const hasWritePerms = permissions.some((p) => p.permission & 2) // 2 = write permission
  if (hasWritePerms) return "operator"

  // Otherwise readonly
  return "readonly"
}

// Page-level cache of every org's full permission list, keyed only by the
// org set — NOT by user. A user-management page renders one
// UserOrganizationsList per user; keying the fetch per user would fan out
// to (users x orgs) concurrent requests and exhaust the backend DB pool
// (each request holds a pooled connection), surfacing as opaque CORS errors
// when the resulting 500s skip the CORS headers. Fetching each org once and
// filtering client-side keeps it to one shared request per org for the whole
// page, regardless of how many users are listed.
interface OrgPermissions {
  orgId: string
  orgName: string
  permissions: ResourcePermissionRead[]
}

export const UserOrganizationsList = ({ user }: UserOrganizationsListProps) => {
  const sc = useSemanticColors()
  // Get all organizations
  const { data: organizations = [], isLoading: orgsLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: () =>
      OrganisationsService.readOrganisations({ skip: 0, limit: 100 }),
  })

  // Fetch each org's full permission list once (shared across every
  // UserOrganizationsList via the org-scoped query key).
  const orgIds = organizations.map((org) => org.id).sort()
  const { data: allOrgPermissions = [], isLoading: permsLoading } = useQuery({
    queryKey: ["all-org-permissions", orgIds],
    queryFn: async () => {
      const result: OrgPermissions[] = []
      for (const org of organizations) {
        try {
          const permissions =
            await OrganisationsService.getOrganizationPermissions({
              organisationId: org.id,
            })
          result.push({
            orgId: org.id,
            orgName: org.name,
            permissions: permissions ?? [],
          })
        } catch (error) {
          console.error(`Failed to get permissions for org ${org.id}:`, error)
        }
      }
      return result
    },
    enabled: organizations.length > 0 && !user.is_superuser,
  })

  // Narrow the shared per-org permissions down to this user.
  const orgPermissions = allOrgPermissions
    .map(({ orgId, orgName, permissions }) => ({
      orgId,
      orgName,
      permissions: permissions.filter((p) => p.user_id === user.id),
    }))
    .filter(({ permissions }) => permissions.length > 0)

  if (user.is_superuser) {
    return (
      <VStack align="start" gap={1} mt={2}>
        <Badge colorScheme="purple" size="sm">
          Superuser - Access to all organizations
        </Badge>
      </VStack>
    )
  }

  if (orgsLoading || permsLoading) {
    return (
      <Box mt={2}>
        <Spinner size="sm" color={sc.spinnerColor} />
      </Box>
    )
  }

  if (orgPermissions.length === 0) {
    return (
      <Text fontSize="xs" color={sc.textSubtle} mt={2}>
        No organization memberships
      </Text>
    )
  }

  return (
    <VStack align="start" gap={2} mt={2}>
      <Text fontSize="xs" color={sc.textMuted} fontWeight="500">
        Organizations:
      </Text>
      {orgPermissions.map(({ orgId, orgName, permissions }) => {
        const role = getRoleFromPermissions(permissions)
        const colorScheme =
          role === "admin" ? "purple" : role === "operator" ? "orange" : "green"

        return (
          <HStack key={orgId} gap={2}>
            <Text fontSize="xs" color={sc.textSecondary}>
              {orgName}
            </Text>
            <Badge colorScheme={colorScheme} size="sm">
              {role}
            </Badge>
          </HStack>
        )
      })}
    </VStack>
  )
}
